import { isDefined } from '@sgme/fp';
import type { Day, Frequency } from 'apiConnector/scheduledRun/model';
import { FieldValueListLayout } from 'components/shared/FieldValueListNext/FieldValueListLayout';
import { DaysTriggerDisplay } from 'components/shared/ScheduledRun/shared/DaysTriggerDisplay';
import { FrequencyDisplay } from 'components/shared/ScheduledRun/shared/FrequencyDisplay';
import { ConnectedReadersPicker } from 'components/shared/ScheduledRun/shared/ReadersPicker';
import { TriggerTimeDisplay } from 'components/shared/ScheduledRun/shared/TriggerTimeDisplay';
import { useIntl } from 'react-intl';
import { connect, type MapStateToProps } from 'react-redux';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID, getEditableScheduledRun } from 'state/blotters/scheduledQuotes';
import { getScheduledFieldsErrors } from 'state/scheduledRuns/selectors';
import type { TriggerMode } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import { displayDateFormatOptions } from 'utils/date';

type StateProps = {
  triggerMode: TriggerMode;

  time?: string;

  timeZone: string;

  frequency: Frequency;

  days: readonly Day[];

  startDate?: string;
  endDate?: string;
};

type Props = StateProps;

const mapStateToProps: MapStateToProps<StateProps, {}, AppState> = app => {
  const run = getEditableScheduledRun(app);
  return {
    triggerMode: run.triggerMode,
    time: run.time,
    errors: getScheduledFieldsErrors(app, EDITABLE_SCHEDULED_RUN_BLOTTER_ID),
    timeZone: app.scheduledRun.timeZone,
    updateRun: run?.updateRun,
    isEditing: run.type === 'editSchedule',
    frequency: run.frequency,
    days: run.days,
    startDate: run.range?.startDate,
    endDate: run.range?.endDate,
  };
};
export const ConnectedSchedulesTabDisplay = connect(mapStateToProps)(SchedulesTabDisplay);
export function SchedulesTabDisplay({ triggerMode, frequency, days, time, timeZone, startDate, endDate }: Props) {
  const { formatDate } = useIntl();
  return (
    <ul className="field-value-list-component list-group list-group-flush" data-e2e="field-value-list">
      <FieldValueListLayout
        translate
        fullWidthLabel
        label="scheduledRun.type"
        valueClassName="text-capitalize"
        value={triggerMode}
      />
      {triggerMode === 'days' ? (
        <>
          <FieldValueListLayout translate fullWidthLabel label="scheduledRun.days">
            <DaysTriggerDisplay days={days} />
          </FieldValueListLayout>
        </>
      ) : (
        <>
          {isDefined(startDate) && isDefined(endDate) && (
            <FieldValueListLayout translate fullWidthLabel label="scheduledRun.scheduleRange">
              <div className="d-flex align-items-center ">
                <em className="icon icon-sm text-secondary me-2">calendar_month</em>
                {formatDate(startDate, displayDateFormatOptions)}
                <em className="icon icon-sm mx-3 text-secondary">arrow_forward</em>
                {formatDate(endDate, displayDateFormatOptions)}
              </div>
            </FieldValueListLayout>
          )}
          <FieldValueListLayout translate fullWidthLabel label="scheduledRun.frequency">
            <FrequencyDisplay type={frequency.type} value={frequency.value} />
          </FieldValueListLayout>
        </>
      )}
      {isDefined(time) && isDefined(timeZone) && (
        <FieldValueListLayout translate label="scheduledRun.triggerTime" fullWidthLabel>
          <TriggerTimeDisplay value={time} timeZone={timeZone} />
        </FieldValueListLayout>
      )}
      <FieldValueListLayout translate label="scheduledRun.distributionList" fullWidthLabel>
        <ConnectedReadersPicker id={EDITABLE_SCHEDULED_RUN_BLOTTER_ID} disabled />
      </FieldValueListLayout>
    </ul>
  );
}
